import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { z } from 'zod'
import type { ResponseByPath } from '@@/api/types.js'
import type { ClientData } from '@/models/client.js'
import { useApi } from '@/composables/useApi.js'
import { useAuth } from '@/composables/useAuth.js'
import useTranslation from '@/composables/useTranslation.js'

export type AddressData = {
  streetAddress?: string
  streetAddress2?: string
  streetAddress3?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
}

export type AddressFormData = {
  street_address: string
  street_address_2: string
  street_address_3: string
  zip_code: string
  city: string
  state: ClientData['state'] | string // Should be a string for non-US countries
  country: ClientData['country']
}

/**
 * Fetch city and state based on ZIP code and country.
 * The fetch is done using the Zippopotam API.
 * @param {string} zipCode - ZIP/postal code.
 * @param {string} countryCode - Two-letter ISO country code (e.g., "US", "ES").
 */
const zipCodeLocationKeys = {
  all: ['zipCodeLocation'] as const,
  lists: () => [...zipCodeLocationKeys.all, 'list'] as const,
  list: (
    query: MaybeRef<{
      zipCode: ClientData['zip_code']
      countryCode: ClientData['country']
    }>,
  ) => ['zipCodeLocation', query] as const,
}

const getZipCodeLocation = async ({
  zipCode,
  countryCode,
}: {
  zipCode: ClientData['zip_code']
  countryCode: ClientData['country']
}) => {
  const response = await fetch(
    `https://api.zippopotam.us/${countryCode}/${zipCode}`,
  )
  if (!response.ok) {
    throw new Error('Failed to fetch city and state')
  }
  return response.json()
}

export function useGetZipCodeLocationQuery(
  zipCode: MaybeRef<ClientData['zip_code']>,
  countryCode: MaybeRef<ClientData['country']>,
  { enabled }: { enabled: MaybeRef<boolean> },
) {
  const queryParams = computed(() => ({
    zipCode: unref(zipCode) || undefined,
    countryCode: unref(countryCode) || undefined,
  }))

  const query = useQuery({
    enabled,
    queryKey: zipCodeLocationKeys.list(queryParams),
    queryFn: () =>
      getZipCodeLocation({
        zipCode: queryParams.value.zipCode,
        countryCode: queryParams.value.countryCode,
      }),
    // To avoid excessive API requests, cache will always considered fresh.
    // This means that the query will not be refetched
    // for the same zip code and country code.
    staleTime: Infinity,
  })

  return reactive(query)
}

/**
 * Fetch list of state/province/region of a Country.
 * Uses the backend country_subdivisions endpoint which returns ISO 3166-2 data.
 * @param {string} countryCode - Two-letter ISO country code (e.g., "US", "ES").
 */

const COUNTRY_SUBDIVISIONS_URL = '/{provet_id}/api/country_subdivisions'

type CountrySubdivisionsResponse = ResponseByPath<
  'get',
  typeof COUNTRY_SUBDIVISIONS_URL
>

const countryRegionKeys = {
  all: ['countryRegion'] as const,
  detail: (countryCode: MaybeRef<string | undefined>) =>
    [...countryRegionKeys.all, 'detail', countryCode] as const,
}

export function useGetCountryRegionsQuery(
  countryCode: MaybeRef<ClientData['country']>,
  { enabled }: { enabled: MaybeRef<boolean> },
) {
  const api = useApi()
  const provetId = useRouteParams<string>('provetId')

  const query = useQuery({
    enabled,
    queryKey: countryRegionKeys.detail(countryCode),
    queryFn: () =>
      api((client) =>
        client.GET(COUNTRY_SUBDIVISIONS_URL, {
          params: {
            path: { provet_id: provetId.value },
            query: { country: unref(countryCode) ?? '' },
          },
        }),
      ),
    select: (response) =>
      (response.data ?? [])
        .map((s) => ({
          label: s.attributes?.translated_name ?? s.attributes?.name ?? '',
          value: s.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    staleTime: Infinity,
  })

  return reactive(query)
}

/**
 * Countries that use the BE `state` field (USStateField, max_length=2).
 */
export const STATE_FIELD_COUNTRIES = ['US'] as const

/**
 * Countries that use the BE `country_region` field (CharField, max_length=100).
 */
export const COUNTRY_REGION_FIELD_COUNTRIES = ['AU', 'CA', 'IT', 'ES'] as const

/**
 * All countries that display a subdivision field in the address form.
 */
export const COUNTRIES_WITH_SUBDIVISIONS = [
  ...STATE_FIELD_COUNTRIES,
  ...COUNTRY_REGION_FIELD_COUNTRIES,
] as const

/**
 * AU uses `country_region` on the BE, but the label is "State".
 */
export const STATE_LABEL_COUNTRIES = [...STATE_FIELD_COUNTRIES, 'AU'] as const

export type CountryWithStateField = (typeof STATE_FIELD_COUNTRIES)[number]
export type CountryWithRegionField =
  (typeof COUNTRY_REGION_FIELD_COUNTRIES)[number]
export type CountryWithSubdivisions =
  (typeof COUNTRIES_WITH_SUBDIVISIONS)[number]
export type StateLabelCountry = (typeof STATE_LABEL_COUNTRIES)[number]

export const useAddressTranslations = () => {
  const tAddressFormComponent = useTranslation('common.addressForm')

  const zipCodeLabel = (country: ClientData['country']) => {
    if (country === 'US') {
      return tAddressFormComponent('fields.zip_code.label')
    }

    return tAddressFormComponent('fields.postal_code.label')
  }

  const stateLabel = (country: ClientData['country']) => {
    if (STATE_LABEL_COUNTRIES.includes(country as StateLabelCountry)) {
      return tAddressFormComponent('fields.state.label')
    }

    if (
      COUNTRY_REGION_FIELD_COUNTRIES.includes(country as CountryWithRegionField)
    ) {
      return tAddressFormComponent('fields.provinceRegion.label')
    }

    return tAddressFormComponent('fields.stateProvinceRegion.label')
  }

  return {
    zipCodeLabel,
    stateLabel,
  }
}
// Generate a type based on AddressData but with all fields being required and non-nullable
export const useAddressValidation = (
  options: {
    skipValidation?: (keyof AddressData)[]
    fieldNames?: Partial<Required<AddressData>>
  } = {
    skipValidation: [],
    fieldNames: {},
  },
) => {
  const tAddressFormComponent = useTranslation('common.addressForm')
  const { activeDepartmentCountry } = useAuth()

  const skipValidation = options.skipValidation || []
  const fieldNames = {
    streetAddress: `${options?.fieldNames?.streetAddress || 'street_address'}`,
    streetAddress2: `${options?.fieldNames?.streetAddress2 || 'street_address_2'}`,
    streetAddress3: `${options?.fieldNames?.streetAddress3 || 'street_address_3'}`,
    zipCode: `${options?.fieldNames?.zipCode || 'zip_code'}`,
    city: `${options?.fieldNames?.city || 'city'}`,
    state: `${options?.fieldNames?.state || 'state'}`,
    country: `${options?.fieldNames?.country || 'country'}`,
  }

  // Checks if the field should be validated
  const shouldValidateField = (key: keyof AddressData) =>
    !skipValidation.includes(key)

  // ZIP Code/Postal Code validators
  const validateUSZipCode = (value: Record<string, unknown>) => {
    if (!shouldValidateField('zipCode')) {
      return true
    }

    const currentCountry = shouldValidateField('country')
      ? value?.[fieldNames.country]
      : activeDepartmentCountry.value
    return !(currentCountry === 'US' && !value.zip_code)
  }
  const validateNonUSPostalCode = (value: Record<string, unknown>) => {
    if (!shouldValidateField('zipCode')) {
      return true
    }

    const currentCountry = shouldValidateField('country')
      ? value?.[fieldNames.country]
      : activeDepartmentCountry.value
    return !(currentCountry !== 'US' && !value.zip_code)
  }

  // State/Province/Region validators
  const validateUSState = (value: Record<string, unknown>) => {
    if (!shouldValidateField('state')) {
      return true
    }

    return !(
      STATE_FIELD_COUNTRIES.includes(
        value?.[fieldNames.country] as CountryWithStateField,
      ) && !value[fieldNames.state]
    )
  }
  const validateRegionCountry = (value: Record<string, unknown>) => {
    if (!shouldValidateField('state')) {
      return true
    }

    return !(
      COUNTRY_REGION_FIELD_COUNTRIES.includes(
        value?.[fieldNames.country] as CountryWithRegionField,
      ) && !value[fieldNames.state]
    )
  }

  return (
    z
      .object({
        ...(shouldValidateField('streetAddress')
          ? {
              [fieldNames.streetAddress]: z
                .string()
                .min(
                  1,
                  tAddressFormComponent(
                    'fields.street_address.validation.required',
                  ),
                ),
            }
          : {}),
        ...(shouldValidateField('streetAddress2')
          ? {
              [fieldNames.streetAddress2]: z.string().optional(),
            }
          : {}),
        ...(shouldValidateField('streetAddress3')
          ? {
              [fieldNames.streetAddress3]: z.string().optional(),
            }
          : {}),
        ...(shouldValidateField('city')
          ? {
              [fieldNames.city]: z
                .string()
                .min(
                  1,
                  tAddressFormComponent('fields.city.validation.required'),
                ),
            }
          : {}),
        ...(shouldValidateField('zipCode')
          ? {
              [fieldNames.zipCode]: z.string().optional(),
            }
          : {}),
        ...(shouldValidateField('state')
          ? {
              [fieldNames.state]: z.string(),
            }
          : {}),
        ...(shouldValidateField('country')
          ? {
              [fieldNames.country]: z
                .string()
                .min(
                  1,
                  tAddressFormComponent('fields.country.validation.required'),
                ),
            }
          : {}),
      })
      // Postal/Zip Code Code validation
      .refine(validateUSZipCode, {
        message: tAddressFormComponent('fields.zip_code.validation.required'),
        path: [fieldNames.zipCode],
      })
      .refine(validateNonUSPostalCode, {
        message: tAddressFormComponent(
          'fields.postal_code.validation.required',
        ),
        path: [fieldNames.zipCode],
      })
      // State validation
      .refine(validateUSState, {
        message: tAddressFormComponent('fields.state.validation.required'),
        path: [fieldNames.state],
      })
      .refine(validateRegionCountry, {
        message: tAddressFormComponent(
          'fields.stateProvinceRegion.validation.required',
        ),
        path: [fieldNames.state],
      })
  )
}

export function useGetStateName() {
  const queryClient = useQueryClient()

  return (stateCode: string, countryCode: string) => {
    if (!stateCode || !countryCode) {
      return ''
    }

    const cached = queryClient.getQueryData(
      countryRegionKeys.detail(countryCode),
    ) as CountrySubdivisionsResponse | undefined

    const subdivision = cached?.data?.find((s) => s.id === stateCode)

    return (
      subdivision?.attributes?.translated_name ??
      subdivision?.attributes?.name ??
      ''
    )
  }
}
